<script setup lang="ts">
import { WarningTriangleIcon } from "@voltalis/ui-kit/icons";
import { storeToRefs } from "pinia";
import { computed, reactive, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import BarChartIcon from "@/assets/icons/bar-chart.svg?component";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EDataUnit } from "@/constants/data-units.type";
import { EMainConsumptionSource } from "@/data/api/kili/consumption/types/common-consumption.type";
import {
  TMultisiteConsumption,
  TMultisiteMissingInfos,
} from "@/data/api/kili/consumption/types/multisite-consumption.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { getMaybeTruncatedNumber } from "@/utils/number";
import { formatWhValue } from "@/utils/translation-utils";

import ConsumptionEvolutionCard from "@/app/data/dashboard/cards/common/consumption-evolution-card.vue";
import MissingDsoSubscriptionDialog from "@/app/data/dashboard/dialog/missing-dso-subscription-dialog.vue";
import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import InsightsCard from "@/components/data/dashboard/insights-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  isConsumptionLoading?: boolean;
  consumption?: TMultisiteConsumption;
  missingInfos?: TMultisiteMissingInfos;
  isMissingDsoSubscriptionWarningVisible?: boolean;
}

interface IEmits {
  (e: "open-missing-subscription-dialog"): void;
  (e: "open-missing-surface-areas-dialog"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const MULTI = 2;
const { t, k } = useTypedIntl();

const dashboardStore = useDashboardStore();
const { insights } = storeToRefs(dashboardStore);
const { isEnvFeatureEnabled } = useFeatureFlag();

const { data: sites } = useGetSitesQuery();

enum EInsightsCards {
  DSO_PER_AREA_CONSUMPTION = "DSO_PER_AREA_CONSUMPTION",
  CONSUMPTION_EVOLUTION = "CONSUMPTION_EVOLUTION",
  ESTIMATED_CONSUMPTION_COST = "ESTIMATED_CONSUMPTION_COST",
}

const isMissingDsoSubscriptionDialogVisible = ref<boolean>();

const insightsCardInfosVisible = reactive({
  [EInsightsCards.DSO_PER_AREA_CONSUMPTION]: false,
  [EInsightsCards.CONSUMPTION_EVOLUTION]: false,
  [EInsightsCards.ESTIMATED_CONSUMPTION_COST]: false,
});

const isMissingDsoSubscriptionWarningVisible = computed<boolean>(() => {
  return (
    !!props.missingInfos?.missingSomeDsoIndexSubscriptions || !!props.missingInfos?.missingAllDsoIndexSubscriptions
  );
});

const averageDsoConsumptionPerSite = computed<string | undefined>(() => {
  if (!props.consumption?.global.value || !sites.value?.length) return;
  return formatWhValue(props.consumption.global.value / sites.value.length);
});

const devicesConsumptionPercent = computed<number | undefined>(() => {
  if (!props.consumption?.global.value || !props.consumption?.totalDevices.value) return;
  return getMaybeTruncatedNumber((props.consumption?.totalDevices.value / props.consumption?.global.value) * 100);
});

const devicesConsmptionCost = computed<number | undefined>(() => {
  if (!devicesConsumptionPercent.value || !props.consumption?.globalCost.value) return;
  return getMaybeTruncatedNumber((props.consumption?.globalCost.value * devicesConsumptionPercent.value) / 100);
});

const averageConsumptionCostPerM2 = computed<number | undefined>(() => {
  if (
    !props.consumption?.global.value ||
    !props.consumption?.globalCost.value ||
    !props.consumption?.totalPerArea.value
  )
    return;
  return getMaybeTruncatedNumber(
    (props.consumption?.totalPerArea.value * props.consumption?.globalCost.value) / props.consumption?.global.value,
  );
});

function handleShowDsoConsumptionInfos(cardToClose: EInsightsCards) {
  insightsCardInfosVisible[cardToClose] = false;
  dashboardStore.showDsoConsumptionInfos();
}
</script>

<template>
  <DashboardCard
    v-bind="$attrs"
    :title="t(k.data.dashboard.insights.title)"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <BarChartIcon />
      </div>
    </template>

    <div class="w-full flex flex-col gap-2">
      <div class="w-full flex gap-2 flex-wrap">
        <InsightsCard
          v-model:infos-visible="insights.isDsoConsumptionInfosVisible"
          class="flex-1 min-w-[300px]"
          :content="formatWhValue(props.consumption?.global.value)"
          :data-test-id="DATA_TEST_IDS.views.dashboard.insights.totalDsoConsumptionCard"
          :infos="t(k.data.dashboard.insights.totalConsumption.tooltip[EDashboardType.MULTISITE])"
          :is-loading="props.isConsumptionLoading"
          :title="t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MULTISITE])"
        >
          <template #bottom>
            <HospitalityButton
              v-if="isMissingDsoSubscriptionWarningVisible"
              class="!text-orange-500"
              :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.missingDsoSubscription.button"
              :icon="WarningTriangleIcon"
              severity="link"
              size="sm"
              @click="emits('open-missing-subscription-dialog')"
            >
              {{ t(k.data.dashboard.insights.totalConsumption.missingDsoSubscription) }}
            </HospitalityButton>
            <span
              v-else
              class="text-text-secondary flex items-center gap-2"
            >
              <template v-if="averageDsoConsumptionPerSite">
                {{
                  t(
                    k.data.dashboard.insights.totalConsumption.subtitle[EDashboardType.MULTISITE]({
                      average: averageDsoConsumptionPerSite,
                    }),
                  )
                }}
              </template>
              <template v-else>—</template>
            </span>
          </template>
        </InsightsCard>
        <InsightsCard
          v-model:infos-visible="insights.isDevicesTotalConsumptionInfosVisible"
          class="flex-1 min-w-[300px]"
          :content="formatWhValue(props.consumption?.totalDevices.value)"
          :data-test-id="DATA_TEST_IDS.views.dashboard.insights.totalDevicesConsumptionCard"
          :infos="t(k.data.dashboard.insights.equipmentConsumption.tooltip[EDashboardType.MULTISITE])"
          :is-loading="props.isConsumptionLoading"
          :title="t(k.data.dashboard.insights.equipmentConsumption.title)"
        >
          <template #bottom>
            <span class="text-text-secondary flex items-center gap-2">
              <template v-if="devicesConsumptionPercent">
                {{
                  t(
                    k.data.dashboard.insights.equipmentConsumption.subtitle({
                      percent: devicesConsumptionPercent,
                    }),
                  )
                }}
              </template>
              <template v-else>—</template>
            </span>
          </template>
        </InsightsCard>
        <InsightsCard
          v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.ESTIMATED_CONSUMPTION_COST]"
          class="flex-1 min-w-[300px]"
          :content="
            t(
              k.global.units[EDataUnit.EURO]({
                value: getMaybeTruncatedNumber(props.consumption?.globalCost.value) ?? '—',
              }),
            )
          "
          :data-test-id="DATA_TEST_IDS.views.dashboard.insights.estimatedConsumptionCostCard"
          :is-loading="props.isConsumptionLoading"
          :title="t(k.data.dashboard.insights.totalCost.title[props.consumption?.mainConsumptionSource ?? 'fallback'])"
        >
          <template #infos>
            <i18n-t
              class="whitespace-pre-line"
              :keypath="keyOf(k.data.dashboard.insights.totalCost.tooltip)"
              tag="p"
            >
              <template #sitesConsumption>
                <HospitalityButton
                  severity="link"
                  size="xs"
                  :data-test-id="DATA_TEST_IDS.views.dashboard.links.totalDsoConsumptionLink"
                  @click="handleShowDsoConsumptionInfos(EInsightsCards.ESTIMATED_CONSUMPTION_COST)"
                  >{{
                    t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MULTISITE])
                  }}</HospitalityButton
                >
              </template>

              <template #sitesManagementTab>
                <router-link
                  :data-test-id="DATA_TEST_IDS.views.dashboard.links.sitesManagementLink"
                  target="_blank"
                  :to="{
                    name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                      ? ERouteName.SECTION_MANAGEMENT_NEW
                      : ERouteName.SECTION_MANAGEMENT,
                  }"
                >
                  <HospitalityButton
                    severity="link"
                    size="xs"
                  >
                    {{ t(k.components.layout.sideMenu.sitesManagement({ count: MULTI })) }}</HospitalityButton
                  >
                </router-link>
              </template>
            </i18n-t>
          </template>

          <template #bottom>
            <span
              v-if="props.consumption?.mainConsumptionSource === EMainConsumptionSource.DSO"
              class="text-text-secondary flex items-center gap-2"
            >
              <template v-if="devicesConsmptionCost">
                {{ t(k.data.dashboard.insights.totalCost.subtitle({ amount: devicesConsmptionCost })) }}
              </template>
              <template v-else>—</template>
            </span>
          </template>
        </InsightsCard>
      </div>

      <div class="w-full flex gap-2 flex-wrap">
        <InsightsCard
          v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.DSO_PER_AREA_CONSUMPTION]"
          class="flex-1 min-w-[300px]"
          :content="formatWhValue(props.consumption?.totalPerArea.value)"
          :data-test-id="DATA_TEST_IDS.views.dashboard.insights.avgConsumptionPerM2Card"
          :is-loading="isConsumptionLoading"
          :title="t(k.data.dashboard.insights.m2Consumption.title)"
        >
          <template #infos>
            <i18n-t
              class="whitespace-pre-line"
              :keypath="
                keyOf(
                  k.data.dashboard.insights.m2Consumption.tooltip[
                    props.consumption?.mainConsumptionSource ?? 'fallback'
                  ],
                )
              "
              tag="p"
            >
              <template #sitesConsumption>
                <HospitalityButton
                  severity="link"
                  size="xs"
                  :data-test-id="DATA_TEST_IDS.views.dashboard.links.totalDsoConsumptionLink"
                  @click="handleShowDsoConsumptionInfos(EInsightsCards.DSO_PER_AREA_CONSUMPTION)"
                  >{{
                    t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MULTISITE])
                  }}</HospitalityButton
                >
              </template>

              <template #sitesManagementTab>
                <router-link
                  :data-test-id="DATA_TEST_IDS.views.dashboard.links.sitesManagementLink"
                  target="_blank"
                  :to="{ name: ERouteName.SECTION_MANAGEMENT }"
                >
                  <HospitalityButton
                    severity="link"
                    size="xs"
                  >
                    {{ t(k.components.layout.sideMenu.sitesManagement({ count: MULTI })) }}</HospitalityButton
                  >
                </router-link>
              </template>
            </i18n-t>
          </template>

          <template #bottom>
            <HospitalityButton
              v-if="props.missingInfos?.missingAreaSites.length"
              class="!text-orange-500"
              :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.multi.missingSurfaceArea.button"
              :icon="WarningTriangleIcon"
              severity="link"
              size="sm"
              @click="emits('open-missing-surface-areas-dialog')"
            >
              {{ t(k.data.dashboard.insights.m2Consumption.missingSurfaceArea({ count: sites?.length ?? MULTI })) }}
            </HospitalityButton>
            <span
              v-else
              class="text-text-secondary flex items-center gap-2"
            >
              <template v-if="averageConsumptionCostPerM2">
                {{ t(k.data.dashboard.insights.m2Consumption.subtitle({ amount: averageConsumptionCostPerM2 })) }}
              </template>
              <template v-else>—</template>
            </span>
          </template>
        </InsightsCard>
        <ConsumptionEvolutionCard
          v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.CONSUMPTION_EVOLUTION]"
          class="flex-1 min-w-[300px]"
          :data-test-id="DATA_TEST_IDS.views.dashboard.insights.consumptionEvolutionCard"
          :is-loading="isConsumptionLoading"
          :title="
            consumption?.mainConsumptionSource === 'DSO'
              ? t(
                  k.data.dashboard.insights.consumptionEvolution.title[consumption.mainConsumptionSource]({
                    count: sites?.length ?? MULTI,
                  }),
                )
              : t(
                  k.data.dashboard.insights.consumptionEvolution.title[
                    consumption?.mainConsumptionSource ?? 'fallback'
                  ],
                )
          "
          :type="EDashboardType.MULTISITE"
          :value="props.consumption?.totalEvolution.value"
        >
          <template #infos>
            <i18n-t
              class="whitespace-pre-line"
              :keypath="
                keyOf(
                  k.data.dashboard.insights.consumptionEvolution.tooltip[
                    props.consumption?.mainConsumptionSource ?? 'fallback'
                  ],
                )
              "
              tag="p"
            >
              <template #sitesConsumption>
                <HospitalityButton
                  severity="link"
                  size="xs"
                  :data-test-id="DATA_TEST_IDS.views.dashboard.links.totalDsoConsumptionLink"
                  @click="handleShowDsoConsumptionInfos(EInsightsCards.CONSUMPTION_EVOLUTION)"
                  >{{
                    t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MULTISITE])
                  }}</HospitalityButton
                >
              </template>
            </i18n-t>
          </template>
        </ConsumptionEvolutionCard>
      </div>
    </div>
  </DashboardCard>

  <MissingDsoSubscriptionDialog
    v-model:visible="isMissingDsoSubscriptionDialogVisible"
    :has-dso-data="!!props.consumption?.global.value"
    :type="EDashboardType.MULTISITE"
  />
</template>
